import { Component } from '@theme/component';

/**
 * Expands/collapses custom category badges on product cards.
 *
 * @extends {Component<Record<string, never>>}
 */
class CustomCategoryBadges extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#connect();
  }

  disconnectedCallback() {
    this.#disconnect();
    super.disconnectedCallback();
  }

  #defaultVisibleCount = 2;
  #maxVisibleCount = 3;
  #collapseDelayMs = 290;

  /** @type {HTMLElement | null} */
  #cardGallery = null;

  /** @type {ResizeObserver | null} */
  #ro = null;

  #onEnter = () => {
    this.#setExpanded(true);
    this.#relayout();
  };

  #onLeave = () => {
    this.#setExpanded(false);
    window.setTimeout(() => this.#relayout(), this.#collapseDelayMs);
  };

  #onFocusOut = (e) => {
    if (!this.#cardGallery) return;
    if (!this.#cardGallery.contains(e.relatedTarget)) this.#onLeave();
  };

  #connect() {
    this.#cardGallery = this.closest('.card-gallery');
    if (!this.#cardGallery) return;

    this.#setExpanded(false);
    this.#relayout();

    this.#ro = new ResizeObserver(() => this.#relayout());
    this.#ro.observe(this.#cardGallery);

    this.#cardGallery.addEventListener('pointerenter', this.#onEnter);
    this.#cardGallery.addEventListener('pointerleave', this.#onLeave);
    this.#cardGallery.addEventListener('focusin', this.#onEnter);
    this.#cardGallery.addEventListener('focusout', this.#onFocusOut);
  }

  #disconnect() {
    if (this.#ro) this.#ro.disconnect();
    this.#ro = null;

    if (!this.#cardGallery) return;

    this.#cardGallery.removeEventListener('pointerenter', this.#onEnter);
    this.#cardGallery.removeEventListener('pointerleave', this.#onLeave);
    this.#cardGallery.removeEventListener('focusin', this.#onEnter);
    this.#cardGallery.removeEventListener('focusout', this.#onFocusOut);
    this.#cardGallery = null;
  }

  #setExpanded(expanded) {
    this.dataset.expanded = expanded ? 'true' : 'false';
  }

  #showEnter(el) {
    if (!el.hidden) return;
    el.hidden = false;
    el.classList.add('is-entering');
    requestAnimationFrame(() => el.classList.remove('is-entering'));
  }

  #relayout() {
    requestAnimationFrame(() => this.#layout());
  }

  #layout() {
    const pills = Array.from(this.querySelectorAll('[data-pill]'));
    const plus = this.querySelector('[data-plus]');

    if (!plus || pills.length === 0) return;

    const expanded = this.dataset.expanded === 'true';

    if (expanded) {
      for (const el of pills) this.#showEnter(el);
      plus.hidden = true;
      return;
    }

    for (let idx = 0; idx < pills.length; idx++) {
      pills[idx].hidden = idx >= this.#defaultVisibleCount;
    }

    plus.hidden = pills.length <= this.#defaultVisibleCount;

    if (pills.length <= this.#defaultVisibleCount) return;

    const firstRowTop = pills[0].offsetTop;
    let visibleCount = this.#defaultVisibleCount;

    for (let i = this.#defaultVisibleCount; i < Math.min(this.#maxVisibleCount, pills.length); i++) {
      pills[i].hidden = false;
      visibleCount = i + 1;

      if (pills[i].offsetTop > firstRowTop) {
        pills[i].hidden = true;
        visibleCount -= 1;
        break;
      }
    }

    if (plus.hidden) return;

    if (plus.offsetTop > firstRowTop) {
      const lastVisibleIndex = visibleCount - 1;
      if (lastVisibleIndex >= 0) pills[lastVisibleIndex].hidden = true;
    }
  }
}

if (!customElements.get('custom-category-badges')) {
  customElements.define('custom-category-badges', CustomCategoryBadges);
}

